import moment from "moment";
import { getLogLevel, LogLevel } from "../../../Utilities/Platform/Logging";
import { isInUploadWorkerContext } from "../../../Utilities/Platform/WorkerHelpers";
import { type BaseSalesRepository } from "../../_Bases/BaseSalesRepository";
import { type LocalDatabaseSale } from "../../_Bases/BaseSalesRepository";
import { type InternalIndexedDBDatabase } from "../../DataStores/Database";
import BaseIndexedDBRepository from "../../Implementations/BaseIndexedDBRepository";

export default class IndexedDBSalesRepository extends BaseIndexedDBRepository<
    LocalDatabaseSale
> implements BaseSalesRepository {
    public readonly source: InternalIndexedDBDatabase["sales"];
    protected readonly dataTypeName = "Sale";

    constructor(source: InternalIndexedDBDatabase) {
        super();

        this.source = source.sales;

        void this.clearOldSales();
    }

    public parked(): Promise<Array<LocalDatabaseSale>> {
        return this.source
            .where("status")
            .equals("PARKED")
            .toArray();
    }

    public getByClientId(id: string): Promise<Array<LocalDatabaseSale>> {
        return this.source
            .where("clientId")
            .equals(id)
            .toArray();
    }

    public search(value: string): Promise<Array<LocalDatabaseSale>> {
        return this.source.where("invoiceId").equals(value).toArray();
    }

    public filter(callback: (sale: LocalDatabaseSale) => boolean): Promise<Array<LocalDatabaseSale>> {
        return this.source.filter(callback).toArray();
    }

    protected async clearOldSales(): Promise<void> {
        if(!isInUploadWorkerContext()) {
            return;
        }

        const compareDate = moment().subtract(10, "days");

        if(getLogLevel() === LogLevel.VERBOSE) {
            console.log("Looking for old sales to clear");
        }

        const cleared = await this.source.where("status")
            .anyOf([ "COMPLETED", "INCOMPLETE" ])
            .filter(sale => sale.uploaded && compareDate.isAfter(sale.createdAt))
            .delete();

        if(cleared > 0) {
            console.log(`Cleared ${cleared} sales`);
        } else if(getLogLevel() === LogLevel.VERBOSE) {
            console.log("Cleared 0 sales");
        }
    }
}
